import { identity } from 'lodash';
import React from 'react';
import { addItestID } from 'src/itest-utils/addItestID';
import { ButtonColors } from 'src/shared/design-system/Buttons';
import {
  DateIcon,
  DateIconContainer,
  DatePickerContainer,
  DatePickerInputContainer,
} from 'src/shared/design-system/DateInput';
import { Dropdown, ItemProps } from 'src/shared/design-system/Dropdown';
import { processImage } from 'src/shared/design-system/FileUpload/filePreprocessor';
import { Loading } from 'src/shared/design-system/Loading';
import { PhotoUpload as PUploader } from 'src/shared/design-system/PhotoUpload';
import { Tooltip } from 'src/shared/design-system/Tooltip';
import Notifications from 'src/shared/Notifications';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { lazyRetry } from 'src/util/retry';
import styled, { css } from 'styled-components';
import { isString } from 'util';
import { Colors, Font, Mixins, Spacing } from 'web-common/src/shared/styles';
import { DropzoneUploadDisplay } from '../../vendor/DropzoneUploadDisplay';

const Toggle = lazyRetry(() => import('src/shared/design-system/Toggle'));
const PhoneInput = lazyRetry<any>(() => import('src/shared/design-system/PhoneInputWithCountry'));
const DatePicker = lazyRetry(() => import('src/shared/design-system/DateInput/DatePickerInput'));
const DropzoneS3Uploader = lazyRetry<any>(() => import('react-dropzone-s3-uploader'));

export const InputLabel = styled.label`
  font-family: ${Font.Family.SANS} !important;
`;

export enum InputType {
  TEXT = 'text',
  EMAIL = 'email',
  PASSWORD = 'password',
  NUMBER = 'number',
  CURRENCY = 'currency',
  DATE = 'date',
  TEXT_AREA = 'text_area',
  SELECT = 'select',
  DROPDOWN = 'dropdown',
  FILE = 'file',
  CHECKBOX = 'checkbox',
  TOGGLE = 'toggle',
  LOCATION = 'location',
  IMAGE = 'image',
  VIDEO = 'video',
  PHONE = 'phone',
  RADIO = 'radio',
}

interface IBaseProps<T> {
  name?: string;
  placeholder?: string;
  label?: React.ReactNode;
  tooltipText?: string;
  checkboxContent?: React.ReactNode;
  radioContent?: React.ReactNode;
  inputProps?: any;

  filter?: (val: any) => boolean;
  onChange?: (val: T) => any;
  onBlur?: () => void;
  onEnter?: () => any;

  value?: any;
  defaultValue?: any;

  wrapperClass?: string;
  wrapperStyle?: React.CSSProperties;

  className?: string;
  inputClass?: string;
  inputStyle?: React.CSSProperties;

  addonItem?: React.ReactNode;
  maxlength?: number;
  accept?: string;
  customWidth?: string;

  dropdownItems?: ItemProps[];
  dateFormat?: string;
  hideTime?: boolean;
  maxDate?: Date;
  minDate?: Date;
  noPadding?: boolean;

  disabled?: boolean;
  isError?: boolean;

  initialFocus?: boolean;
  inputMode?: string;

  /**
   * A string which identifies the actual input element. This string does not necessarily have to be unique --
   * You might want to select all of the checkboxes associated with a parent element, for instance.
   */
  itestID?: string;

  /* Uses new photo uploader for images. TODO: Deprecate DropzoneS3Uploader. */
  useNewImageUploader?: boolean;
}

type InputText = IBaseProps<string> & { type: InputType.TEXT };
type InputTextArea = IBaseProps<string> & { type: InputType.TEXT_AREA };
type InputEmail = IBaseProps<string> & { type: InputType.EMAIL };
type InputPassword = IBaseProps<string> & { type: InputType.PASSWORD };
type InputNumber = IBaseProps<number> & { type: InputType.NUMBER };
type InputCurrency = IBaseProps<number> & { type: InputType.CURRENCY };
type InputDate = IBaseProps<Date> & { type: InputType.DATE };
type InputFile = IBaseProps<File> & { type: InputType.FILE };
type InputS3 = {
  s3Url?: string;
  s3Path?: string;
  signingUrl?: string;
  scrubFilename?: (filename: string) => string;
};
type InputImage = IBaseProps<string> & InputS3 & { type: InputType.IMAGE };
type InputVideo = IBaseProps<string> & InputS3 & { type: InputType.VIDEO };
type InputCheckbox = IBaseProps<boolean> & { type: InputType.CHECKBOX };
type InputToggle = IBaseProps<boolean> & { type: InputType.TOGGLE };
type InputSelect = IBaseProps<number | string> & { type: InputType.SELECT };
type InputDropdown = IBaseProps<number | string> & { type: InputType.DROPDOWN };
type InputPhone = IBaseProps<string> & { type: InputType.PHONE };
type InputRadio = IBaseProps<boolean> & { type: InputType.RADIO };

type IProps =
  | InputText
  | InputPassword
  | InputEmail
  | InputNumber
  | InputCurrency
  | InputDate
  | InputTextArea
  | InputFile
  | InputImage
  | InputCheckbox
  | InputToggle
  | InputSelect
  | InputDropdown
  | InputPhone
  | InputRadio
  | InputVideo;

interface IAppInputState {
  autocompleteText?: string;
  cachedValue: any;
  preprocessing: boolean;
}

const Input = styled.input<{ noPadding?: boolean }>`
  box-shadow: none !important;
  width: 100%;
  box-sizing: border-box;
  ${ifProp(
    'noPadding',
    css`
      padding: 0;
    `
  )}
  &,
  &.form-control {
    ${Mixins.Input.TextStyles}
    height: auto;
  }
`;

const Phone = styled(PhoneInput)`
  margin-bottom: ${Spacing.SINGLE};
  img {
    display: block;
  }
  input {
    ${Mixins.Input.TextStyles}
    padding-left: ${Spacing.TRIPLE_HALF};
  }

  .PhoneInputCountry {
    padding-left: ${Spacing.THREE_QUARTERS};
    margin-right: -${Spacing.TRIPLE};
    width: ${Spacing.TRIPLE};
  }
`;

export const PhotoUpload = styled(PUploader).attrs(() => ({
  autoUpload: true,
  buttonColor: ButtonColors.RED,
  photoSize: 200,
}))`
  align-items: flex-start;
  display: inline-flex;
  margin-bottom: ${Spacing.SINGLE};
  width: 100%;
  max-width: 200px;
  & button {
    max-width: 200px;
  }
`;

export default class AppInput extends React.Component<React.PropsWithChildren<IProps>, IAppInputState> {
  constructor(props: any) {
    super(props);
    this.state = { cachedValue: '', preprocessing: false };
  }

  private _handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { onEnter } = this.props;

    if (e.key === 'Enter') {
      onEnter && onEnter();
    }
  };

  private handleChange = (e: any) => {
    const { type, onChange, disabled, filter = () => true } = this.props;

    if (disabled) {
      return;
    }

    const isCurrency = type === InputType.CURRENCY;

    if (onChange) {
      let cachedValue;

      if (
        type === InputType.FILE ||
        type === InputType.IMAGE ||
        type === InputType.VIDEO ||
        type === InputType.DATE ||
        type === InputType.DROPDOWN ||
        type === InputType.PHONE
      ) {
        cachedValue = e;
      } else if (type === InputType.CHECKBOX || type === InputType.TOGGLE) {
        cachedValue = e.target.checked;
      } else {
        cachedValue = e.target.value;
      }

      if (type === InputType.NUMBER) {
        cachedValue = parseFloat(cachedValue);
      } else if (isCurrency) {
        cachedValue = parseFloat(cachedValue);
      }

      if (!filter(cachedValue)) {
        return;
      }

      this.setState({ cachedValue });
      const callbackValue = isCurrency ? Math.trunc(cachedValue * 100) : cachedValue;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (onChange as any)(callbackValue);
    }
  };

  private preprocessUpload = (blob: File, next: (img: any) => void) => {
    if (this.props.type === InputType.IMAGE) {
      this.setState({ preprocessing: true });

      // file processor loads heic2any, which is massive. We do NOT want
      // this code in the main bundle.
      processImage(blob)
        .then((outputBlob) => {
          next(outputBlob);
        })
        .catch((e) => {
          console.error(e);
          Notifications.error('There was a problem uploading your file');
        })
        .finally(() => {
          this.setState({ preprocessing: false });
        });
    } else {
      next(blob);
    }
  };

  public render() {
    const {
      type,
      name,
      label,
      placeholder,
      addonItem,
      maxlength,
      inputClass,
      inputStyle,
      wrapperStyle,
      wrapperClass,
      customWidth,
      value,
      defaultValue,
      noPadding,
      disabled,
      isError,
      checkboxContent,
      dateFormat,
      hideTime,
      maxDate,
      minDate,
      inputProps,
      dropdownItems,
      className,
      radioContent,
      initialFocus,
      inputMode,
      tooltipText,
      useNewImageUploader,
      onBlur,
    } = this.props;
    const { cachedValue } = this.state;

    let controlledValue = cachedValue;

    if (value !== undefined) {
      controlledValue = value;

      if (type === InputType.CURRENCY) {
        controlledValue = value / 100.0;
      }
    }

    return (
      <div
        className={`app-input ${noPadding ? 'no-padding' : ''} ${wrapperClass || ''}`}
        style={{ padding: '3px 0', ...wrapperStyle }}>
        {typeof label === 'string' ? <InputLabel style={{ marginTop: 5 }}>{label}</InputLabel> : label || null}
        {type !== InputType.RADIO && tooltipText && (
          <Tooltip content={tooltipText} showInfoCircle positions={['right', 'top', 'bottom', 'left']} />
        )}
        {(type === InputType.TEXT ||
          type === InputType.EMAIL ||
          type === InputType.PASSWORD ||
          type === InputType.NUMBER ||
          type === InputType.CURRENCY) && (
          <div className={`input-group`} style={{ width: !customWidth ? '100%' : customWidth }}>
            {addonItem && <span className='input-group-addon'>{addonItem}</span>}

            {addItestID(
              this.props.itestID,
              <Input
                onBlur={onBlur}
                autoFocus={initialFocus}
                type={type.replace('currency', 'number')}
                className={`form-control ${inputClass}`}
                value={controlledValue}
                placeholder={placeholder || (typeof label === 'string' ? label : undefined)}
                name={name}
                onChange={this.handleChange}
                noPadding={noPadding}
                style={inputStyle}
                disabled={disabled}
                onKeyDown={this._handleKeyDown}
                inputMode={inputMode || (type === InputType.NUMBER ? 'numeric' : '')}
                {...inputProps}
              />
            )}
            {this.props.children}
          </div>
        )}
        {type === InputType.TEXT_AREA && (
          <div className={className}>
            {addItestID(
              this.props.itestID,
              <textarea
                onBlur={onBlur}
                autoFocus={initialFocus}
                onKeyDown={this._handleKeyDown}
                maxLength={maxlength}
                value={controlledValue || ''} // Warnings will arrise if we use null for value
                placeholder={placeholder || (typeof label === 'string' ? label : undefined)}
                onChange={this.handleChange}
                style={{
                  width: '100%',
                  borderRadius: 4,
                  padding: noPadding ? 'none' : '25px',
                  backgroundColor: Colors.GRAY_LITE_30,
                  border: `1px solid ${Colors.GRAY_LITE_30}`,
                  borderStyle: 'solid',
                  borderColor: isError ? Colors.RADISH : Colors.GRAY_LITE_30,
                  boxShadow: `inset 0px 0px 0px 0px ${Colors.GRAY_LITE_30}`,
                  boxSizing: 'border-box',
                  ...inputStyle,
                }}
                className={`form-control`}
                rows={3}
                disabled={disabled}
              />
            )}
          </div>
        )}
        {type === InputType.DATE &&
          addItestID(
            this.props.itestID,
            <DatePickerContainer>
              <DatePickerInputContainer light>
                <DateIconContainer>
                  <DateIcon />
                </DateIconContainer>
                <DatePicker
                  onBlur={onBlur}
                  autoFocus={initialFocus}
                  maxDate={maxDate}
                  minDate={minDate}
                  selected={
                    controlledValue && isString(controlledValue)
                      ? new Date(controlledValue)
                      : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        (controlledValue as Date)
                  }
                  showTimeSelect={!hideTime}
                  dateFormat={dateFormat || `MM/dd/yyyy ${hideTime ? '' : 'h:mm aa'}`}
                  onChange={(date: Date) => {
                    if (date.valueOf()) {
                      this.handleChange(date);
                    }
                  }}
                  placeholderText={placeholder}
                  onChangeRaw={(event: any) => this.handleChange(new Date(event.target.value))}
                  timeIntervals={15}
                />
              </DatePickerInputContainer>
            </DatePickerContainer>
          )}
        {type === InputType.TOGGLE &&
          addItestID(
            this.props.itestID,
            <div className='input-group'>
              <Toggle checked={controlledValue} onChange={(_, e) => this.handleChange(e)} onBlur={onBlur} />
            </div>
          )}
        {type === InputType.SELECT &&
          addItestID(
            this.props.itestID,
            <select
              onBlur={onBlur}
              autoFocus={initialFocus}
              style={this.props.inputStyle}
              className={`form-control ${className}`}
              defaultValue={defaultValue}
              value={controlledValue}
              onChange={this.handleChange}
              disabled={disabled}>
              {this.props.children}
            </select>
          )}
        {type === InputType.DROPDOWN &&
          addItestID(
            this.props.itestID,
            <Dropdown
              className={className}
              value={controlledValue}
              onChange={this.handleChange}
              placeholder={placeholder}
              items={dropdownItems}
              disabled={disabled}
            />
          )}
        {type === InputType.CHECKBOX && (
          <div className={`checkbox ${className}`}>
            <InputLabel>
              {addItestID(
                this.props.itestID,
                <input
                  type='checkbox'
                  defaultChecked={value}
                  onChange={this.handleChange}
                  disabled={disabled}
                  onBlur={onBlur}
                  autoFocus={initialFocus}
                />
              )}
              <span style={{ marginLeft: 2 }}>{placeholder || checkboxContent}</span>
            </InputLabel>
          </div>
        )}
        {type === InputType.RADIO && (
          <div className={className ? `radio ${className}` : 'radio'}>
            <InputLabel>
              {addItestID(
                this.props.itestID,
                <input
                  type='radio'
                  value={value || ''}
                  checked={defaultValue}
                  onChange={this.handleChange}
                  disabled={disabled}
                  onBlur={onBlur}
                  autoFocus={initialFocus}
                />
              )}
              <div>
                {placeholder || radioContent}
                {tooltipText && (
                  <Tooltip content={tooltipText} showInfoCircle positions={['right', 'top', 'bottom', 'left']} />
                )}
              </div>
            </InputLabel>
          </div>
        )}

        {this.props.type === InputType.IMAGE &&
          useNewImageUploader &&
          addItestID(
            this.props.itestID,
            <div className={className}>
              <PhotoUpload onChange={this.handleChange} value={controlledValue} />
            </div>
          )}

        {((this.props.type === InputType.IMAGE && !useNewImageUploader) || this.props.type === InputType.VIDEO) &&
          addItestID(
            this.props.itestID,
            <div className={className}>
              <DropzoneS3Uploader
                onFinish={(info: any) => {
                  console.log('File uploaded with filename', info.filename);
                  console.log('Access it on s3 at', info.fileUrl);
                  this.handleChange(info.fileUrl);
                }}
                s3Url={this.props.s3Url ?? 'https://shef-general.s3.amazonaws.com'}
                maxSize={(type === InputType.VIDEO ? 1000 : 30) * 1024 * 1024} // 30mb for images, 1gb for videos
                progressComponent={() => <Loading />}
                upload={{
                  server: process.env.REACT_APP_API_URL,
                  accept: type === InputType.VIDEO ? 'video/*' : 'image/*',
                  preprocess: this.preprocessUpload,
                  signingUrlQueryParams: {
                    path: this.props.s3Path ?? 'uploads/',
                  },
                  scrubFilename: this.props.scrubFilename ?? identity,
                  signingUrl: this.props.signingUrl ?? '/s3/sign',
                }}
                uploadRequestHeaders={{}}
                disabled={disabled}>
                {type === InputType.VIDEO ? (
                  <DropzoneUploadDisplay videoUrl={controlledValue} />
                ) : (
                  <DropzoneUploadDisplay preprocessing={this.state.preprocessing} imageUrl={controlledValue} />
                )}
              </DropzoneS3Uploader>
            </div>
          )}
        {type === InputType.PHONE &&
          addItestID(
            this.props.itestID,
            <Phone
              placeholder={placeholder || label}
              value={controlledValue}
              disabled={disabled}
              country='US'
              onBlur={onBlur}
              autoFocus={initialFocus}
              onChange={this.handleChange}
              style={{
                width: '100%',
                ...inputStyle,
              }}
            />
          )}
      </div>
    );
  }
}
